export type CommunityBrandColorsInput = {
  primaryColor?: string | null;
  accentColor?: string | null;
  secondaryColor?: string | null;
};

export type CommunityBrandColors = {
  primaryColor: string | null;
  accentColor: string | null;
  secondaryColor: string | null;
};

export type CommunityBrandTheme = CommunityBrandColors & {
  cssVariables: Record<string, string>;
};

const HEX_COLOR_PATTERN = /^#?[0-9a-fA-F]{6}$/;

export function normalizeCommunityBrandColor(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!HEX_COLOR_PATTERN.test(trimmed)) return null;
  const hex = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed;
  return `#${hex.toUpperCase()}`;
}

export function validateCommunityBrandColors(input: CommunityBrandColorsInput):
  | {
      ok: true;
      primaryColor: string | null;
      accentColor: string | null;
      secondaryColor: string | null;
    }
  | { ok: false; message: string } {
  const primaryColor =
    input.primaryColor === undefined
      ? undefined
      : normalizeCommunityBrandColor(input.primaryColor);
  const accentColor =
    input.accentColor === undefined
      ? undefined
      : normalizeCommunityBrandColor(input.accentColor);
  const secondaryColor =
    input.secondaryColor === undefined
      ? undefined
      : normalizeCommunityBrandColor(input.secondaryColor);

  if (input.primaryColor !== undefined && input.primaryColor?.trim() && primaryColor == null) {
    return { ok: false, message: 'Primary brand color must be a valid 6-digit hex value' };
  }
  if (input.accentColor !== undefined && input.accentColor?.trim() && accentColor == null) {
    return { ok: false, message: 'Accent brand color must be a valid 6-digit hex value' };
  }
  if (input.secondaryColor !== undefined && input.secondaryColor?.trim() && secondaryColor == null) {
    return { ok: false, message: 'Secondary brand color must be a valid 6-digit hex value' };
  }

  return {
    ok: true,
    primaryColor: primaryColor ?? null,
    accentColor: accentColor ?? null,
    secondaryColor: secondaryColor ?? null,
  };
}

function hexToRgb(hex: string) {
  const normalized = normalizeCommunityBrandColor(hex);
  if (!normalized) return null;
  const value = normalized.slice(1);
  return {
    r: Number.parseInt(value.slice(0, 2), 16),
    g: Number.parseInt(value.slice(2, 4), 16),
    b: Number.parseInt(value.slice(4, 6), 16),
  };
}

export function hexToHslChannels(hex: string): string | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;

  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }

  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function relativeLuminance(hex: string) {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const channels = [rgb.r, rgb.g, rgb.b].map((channel) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0]! + 0.7152 * channels[1]! + 0.0722 * channels[2]!;
}

export function communityBrandForegroundChannels(hex: string): string {
  return relativeLuminance(hex) > 0.58 ? '220 12% 13%' : '0 0% 100%';
}

export function deriveCommunityAccentColor(primaryHex: string): string {
  const rgb = hexToRgb(primaryHex);
  if (!rgb) return primaryHex;
  const mix = (channel: number) =>
    Math.min(255, Math.round(channel + (255 - channel) * 0.72));
  const mixed = `#${[mix(rgb.r), mix(rgb.g), mix(rgb.b)]
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
  return mixed;
}

export function deriveCommunityHeaderTint(primaryHex: string, secondaryHex?: string | null): string {
  const source = normalizeCommunityBrandColor(secondaryHex) ?? normalizeCommunityBrandColor(primaryHex);
  if (!source) return '220 20% 97%';
  const rgb = hexToRgb(source);
  if (!rgb) return '220 20% 97%';
  const mix = (channel: number) =>
    Math.min(255, Math.round(channel + (255 - channel) * 0.92));
  const tinted = `#${[mix(rgb.r), mix(rgb.g), mix(rgb.b)]
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')}`;
  return hexToHslChannels(tinted) ?? '220 20% 97%';
}

export function mapCommunityBrandTheme(colors: CommunityBrandColors): CommunityBrandTheme {
  const primaryColor = normalizeCommunityBrandColor(colors.primaryColor);
  if (!primaryColor) {
    return {
      primaryColor: null,
      accentColor: null,
      secondaryColor: null,
      cssVariables: {},
    };
  }

  const secondaryColor = normalizeCommunityBrandColor(colors.secondaryColor);
  const accentColor =
    normalizeCommunityBrandColor(colors.accentColor) ?? deriveCommunityAccentColor(primaryColor);
  const primary = hexToHslChannels(primaryColor);
  const accent = hexToHslChannels(accentColor);
  const secondary = secondaryColor ? hexToHslChannels(secondaryColor) : null;

  if (!primary || !accent) {
    return {
      primaryColor: null,
      accentColor: null,
      secondaryColor: null,
      cssVariables: {},
    };
  }

  const cssVariables: Record<string, string> = {
    '--primary': primary,
    '--primary-foreground': communityBrandForegroundChannels(primaryColor),
    '--accent': accent,
    '--accent-foreground': '220 12% 13%',
    '--ring': primary,
    '--community-header-tint': deriveCommunityHeaderTint(primaryColor, secondaryColor),
  };

  if (secondary) {
    cssVariables['--secondary'] = secondary;
    cssVariables['--secondary-foreground'] = communityBrandForegroundChannels(
      secondaryColor ?? primaryColor,
    );
  }

  return {
    primaryColor,
    accentColor,
    secondaryColor,
    cssVariables,
  };
}

export function mapCommunityBrandThemeFromRecord(record: {
  brandPrimaryColor?: string | null;
  brandAccentColor?: string | null;
  brandSecondaryColor?: string | null;
}): CommunityBrandTheme {
  return mapCommunityBrandTheme({
    primaryColor: record.brandPrimaryColor ?? null,
    accentColor: record.brandAccentColor ?? null,
    secondaryColor: record.brandSecondaryColor ?? null,
  });
}
